import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge, { STATUS_OPTIONS } from "@/components/admin/StatusBadge";
import DynamicsBadge, { DYNAMICS_FILTER_OPTIONS } from "@/components/admin/DynamicsBadge";
import { listLeads, exportLeadsCsvUrl } from "@/lib/adminApi";

const SOURCE_OPTIONS = ["contact_form", "demo_request", "get_started", "pricing_inquiry", "footer", "seed", "other"];
const VERTICAL_OPTIONS = ["marine", "boat_club", "equipment", "recreation", "powersports", "tours", "events", "other"];
const DATE_PRESETS = [
  { value: "all", label: "All time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminDashboard() {
  useEffect(() => { document.title = "Leads · Admin · RentAnything.ai"; }, []);

  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [vertical, setVertical] = useState("all");
  const [dynStatus, setDynStatus] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [page, setPage] = useState(1);
  const limit = 25;

  const [data, setData] = useState({ items: [], total: 0, page: 1, limit });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const params = useMemo(() => {
    const p = { status, source, vertical, page, limit };
    if (dynStatus !== "all") p.dynamics_status = dynStatus;
    if (qDebounced) p.q = qDebounced;
    if (datePreset !== "all") {
      const days = parseInt(datePreset, 10);
      const from = new Date(Date.now() - days * 86400000).toISOString();
      p.from = from;
    }
    return p;
  }, [status, source, vertical, dynStatus, qDebounced, datePreset, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listLeads(params);
      setData(res);
    } catch (e) {
      toast.error("Couldn't load leads.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, source, vertical, dynStatus, qDebounced, datePreset]);

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  const onExport = () => {
    const exportParams = { ...params };
    delete exportParams.page;
    delete exportParams.limit;
    window.location.href = exportLeadsCsvUrl(exportParams);
    toast.success("CSV download started");
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ra-ocean">Leads</p>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-ra-ink">
            {data.total} lead{data.total === 1 ? "" : "s"} in pipeline
          </h1>
        </div>
        <button
          onClick={onExport}
          className="ra-btn-ghost"
          data-testid="admin-export-csv-btn"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
        <div className="lg:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ra-ink/40" />
          <Input
            placeholder="Search name, business, email…"
            className="pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-search-input"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`admin-filter-status-${s}`}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger data-testid="admin-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {SOURCE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={vertical} onValueChange={setVertical}>
          <SelectTrigger data-testid="admin-filter-vertical"><SelectValue placeholder="Vertical" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All verticals</SelectItem>
            {VERTICAL_OPTIONS.map((v) => (
              <SelectItem key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dynStatus} onValueChange={setDynStatus}>
          <SelectTrigger data-testid="admin-filter-dynamics"><SelectValue placeholder="Dynamics" /></SelectTrigger>
          <SelectContent>
            {DYNAMICS_FILTER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`admin-filter-dynamics-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={datePreset} onValueChange={setDatePreset}>
          <SelectTrigger data-testid="admin-filter-date"><SelectValue placeholder="Date" /></SelectTrigger>
          <SelectContent>
            {DATE_PRESETS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-ra-line bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead className="bg-ra-paper text-left text-[11px] font-semibold uppercase tracking-wider text-ra-ink/55">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Business</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Vertical</th>
                <th className="px-4 py-3">Units</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Sync</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-ra-ink/55">
                    <Loader2 size={16} className="inline animate-spin mr-2" /> Loading…
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-ra-ink/55" data-testid="admin-leads-empty">
                    No leads match your filters.
                  </td>
                </tr>
              ) : (
                data.items.map((l) => (
                  <tr key={l.id} className="border-t border-ra-line hover:bg-ra-paper/60" data-testid={`admin-lead-row-${l.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-ra-ink/70">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-ra-ink">{l.name}</td>
                    <td className="px-4 py-3 text-ra-ink/80">{l.business_name || "—"}</td>
                    <td className="px-4 py-3 text-ra-ink/70">{l.email}</td>
                    <td className="px-4 py-3 capitalize text-ra-ink/80">{l.vertical || "—"}</td>
                    <td className="px-4 py-3 text-ra-ink/70">{l.units || "—"}</td>
                    <td className="px-4 py-3 text-ra-ink/70 capitalize">{(l.source || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-3"><StatusBadge value={l.status} /></td>
                    <td className="px-4 py-3"><DynamicsBadge value={l.dynamics_sync_status} /></td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/leads/${l.id}`}
                        className="text-xs font-semibold text-ra-ocean hover:text-ra-oceanDark"
                        data-testid={`admin-lead-open-${l.id}`}
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-ra-line bg-white px-4 py-3 text-xs text-ra-ink/65">
          <span>
            Page {data.page} of {totalPages} · showing {data.items.length} of {data.total}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={data.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-ra-line bg-white disabled:opacity-40"
              data-testid="admin-page-prev"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              disabled={data.page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-ra-line bg-white disabled:opacity-40"
              data-testid="admin-page-next"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
